// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * The cropped-photograph reveal — the headline finding.
 *
 * Cropping a picture in Word does not remove pixels. It stores the whole
 * original file in `word/media/` and records a rectangle saying which part of
 * it to display. Everything outside that rectangle travels with the document,
 * and there is no way to see it in Word. That is the entire feature: decode the
 * stored file at its real size, draw all of it, and outline the part that was
 * visible.
 *
 * THE ARITHMETIC IS INSETS, NOT COORDINATES. `a:srcRect/@l` is "cut this much
 * off the left edge", not "the left edge is here". Reading the four values as a
 * rectangle produces nonsense the moment both `l` and `r` are non-zero, which
 * is most real crops. A positive value is an inset; a negative one is an
 * OUTSET, i.e. padding around the picture rather than more picture.
 *
 * THE UNITS ARE A UNION AND BOTH BRANCHES ARE LEGAL IN THE SAME ATTRIBUTE.
 * `ST_Percentage` is either an integer in thousandths of a percent (`50000` =
 * 50%, which is what Word writes) or a string with a per-cent sign (`50%` or
 * `12.5%`, which Strict-conformance and some other producers write). A parser
 * that assumes one branch returns NaN on files from the other toolchain.
 *
 * AND MOST `a:srcRect` ELEMENTS ARE NOT CROPS. Measured over a twelve-document
 * corpus: thirteen `a:srcRect` elements, of which ten were empty
 * (`<a:srcRect/>`, every inset defaulting to zero) and only three were real
 * crops. Reporting on the element's presence would flag a crop on nearly every
 * document containing a picture, which is the fastest possible way to make the
 * finding worthless.
 *
 * `a:stretch/a:fillRect` IS THE SAME TYPE AND IS NOT A CROP. It is a
 * DESTINATION rectangle. Matching "any CT_RelativeRect" or "any element with an
 * l/t/r/b" picks it up and produces garbage ratios, so the match is on the
 * element name `srcRect` in the DrawingML namespace, as a child of a blipFill.
 */

import { NS, walkContent } from './wordml';
import { attrAnyNs, attr, type XNode } from './xml';

export interface CropRect {
  /** Fractions of the source cut off each edge. Negative means outset (padding). */
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface PictureRef {
  /** The relationship id holding the media, from `r:embed`, `r:link` or `r:id`. */
  relId: string;
  /** True when the picture is LINKED rather than embedded — the target is a path. */
  linked: boolean;
  crop: CropRect;
  via: 'drawingml' | 'vml';
  /** Alt text / title / original filename Word kept beside the picture. */
  name?: string;
  description?: string;
  /** Display size in EMU, where the drawing recorded one. */
  displayCx?: number;
  displayCy?: number;
}

export const NO_CROP: CropRect = { left: 0, top: 0, right: 0, bottom: 0 };

/**
 * Parses `ST_Percentage` into a fraction.
 *
 * `parseInt` and not `parseFloat` on the integer branch: the integer form is
 * `xsd:int` by definition, and using parseFloat would silently accept `"50.5"`
 * — which is only legal with a per-cent sign — and scale it wrongly.
 */
export function percentage(value: string | undefined): number {
  if (value === undefined) return 0;
  const text = value.trim();
  if (text === '') return 0;
  if (text.endsWith('%')) {
    const n = Number.parseFloat(text.slice(0, -1));
    return Number.isFinite(n) ? n / 100 : 0;
  }
  const n = Number.parseInt(text, 10);
  return Number.isFinite(n) ? n / 100_000 : 0;
}

/**
 * Parses a VML crop attribute.
 *
 * VML types all four as strings precisely because the value may be a bare
 * fraction (`0.25`), a fraction written without its leading zero (`.25`), a
 * percentage (`25%`), or a 1/65536ths fixed-point literal with an `f` suffix.
 * The `f` form is documented for VML numerics generally; whether Word emits it
 * for the crop attributes specifically is unconfirmed, so it is handled
 * defensively rather than relied upon.
 */
export function vmlCrop(value: string | undefined): number {
  if (value === undefined) return 0;
  const text = value.trim();
  if (text === '') return 0;
  if (text.endsWith('%')) {
    const n = Number.parseFloat(text.slice(0, -1));
    return Number.isFinite(n) ? n / 100 : 0;
  }
  if (text.endsWith('f')) {
    const n = Number.parseFloat(text.slice(0, -1));
    return Number.isFinite(n) ? n / 65536 : 0;
  }
  const n = Number.parseFloat(text);
  return Number.isFinite(n) ? n : 0;
}

export function isCropped(crop: CropRect): boolean {
  return crop.left !== 0 || crop.top !== 0 || crop.right !== 0 || crop.bottom !== 0;
}

export interface CropGeometry {
  /** The visible rectangle in SOURCE PIXELS, clamped to the image. */
  x: number;
  y: number;
  width: number;
  height: number;
  /** Total pixels ÷ visible pixels. 1 means nothing is hidden. */
  ratio: number;
  /** Fraction of the stored image that is not displayed, 0..1. */
  hiddenFraction: number;
  /** True when the insets leave nothing visible — report, never divide. */
  degenerate: boolean;
}

/**
 * Turns a crop and the image's real pixel size into the rectangle to outline.
 *
 * Clamping matters and is not defensive tidiness: a negative inset is an outset
 * — the document is padding the picture, not showing more of it — and treating
 * it as extra image would produce a visible area larger than the file and a
 * ratio below 1.
 */
export function cropGeometry(crop: CropRect, width: number, height: number): CropGeometry {
  const clamp = (value: number, max: number) => Math.min(Math.max(value, 0), max);
  const x0 = clamp(crop.left * width, width);
  const y0 = clamp(crop.top * height, height);
  const x1 = clamp((1 - crop.right) * width, width);
  const y1 = clamp((1 - crop.bottom) * height, height);

  const visibleWidth = Math.max(0, x1 - x0);
  const visibleHeight = Math.max(0, y1 - y0);
  const visibleArea = visibleWidth * visibleHeight;
  const totalArea = width * height;

  if (visibleArea <= 0 || totalArea <= 0) {
    return { x: x0, y: y0, width: visibleWidth, height: visibleHeight, ratio: 0, hiddenFraction: 0, degenerate: true };
  }

  return {
    x: x0,
    y: y0,
    width: visibleWidth,
    height: visibleHeight,
    ratio: totalArea / visibleArea,
    hiddenFraction: 1 - visibleArea / totalArea,
    degenerate: false,
  };
}

/**
 * Every picture in a part, with whatever crop it carries.
 *
 * The search is for `pic:blipFill`/`a:blipFill` and `v:imagedata` ANYWHERE in
 * the subtree rather than at a fixed depth, because a picture can hang off an
 * inline drawing, a floating anchor, a shape group nested arbitrarily deep, or
 * a VML shape inside a `w:pict`. `walkContent` collapses the
 * `mc:AlternateContent` branches first, so a picture that Word wrote twice —
 * once as DrawingML and once as VML — is counted once.
 */
export function scanPictures(root: XNode): PictureRef[] {
  const out: PictureRef[] = [];

  walkContent(root, (node) => {
    // DrawingML: `pic:blipFill` in the picture namespace, or a bare `a:blipFill`
    // used by shape fills.
    if ((node.ns === NS.pic || node.ns === NS.a) && node.local === 'blipFill') {
      out.push(...readBlipFill(node));
      return false;
    }

    if (node.ns === NS.v && node.local === 'imagedata') {
      const relId = attr(node, NS.r, 'id') ?? attr(node, NS.r, 'embed');
      const href = attr(node, NS.r, 'href');
      if (relId || href) {
        out.push({
          relId: relId ?? href ?? '',
          linked: relId === undefined,
          via: 'vml',
          // Unprefixed on purpose: VML writes these with no namespace at all.
          crop: {
            left: vmlCrop(attrAnyNs(node, 'cropleft')),
            top: vmlCrop(attrAnyNs(node, 'croptop')),
            right: vmlCrop(attrAnyNs(node, 'cropright')),
            bottom: vmlCrop(attrAnyNs(node, 'cropbottom')),
          },
          name: attrAnyNs(node, 'title'),
        });
      }
      return false;
    }

    return undefined;
  });

  return out;
}

function readBlipFill(blipFill: XNode): PictureRef[] {
  const blip = blipFill.children.find((c) => c.ns === NS.a && c.local === 'blip');
  if (!blip) return [];
  const embed = attr(blip, NS.r, 'embed');
  const link = attr(blip, NS.r, 'link');
  const relId = embed ?? link;
  if (!relId) return [];

  // `srcRect` must be a direct child of the blipFill and in the DrawingML
  // namespace. `a:stretch/a:fillRect` has exactly the same four attributes and
  // exactly the same type, and is a destination rectangle rather than a crop.
  const srcRect = blipFill.children.find((c) => c.ns === NS.a && c.local === 'srcRect');

  return [
    {
      relId,
      linked: embed === undefined,
      via: 'drawingml',
      crop: srcRect
        ? {
            left: percentage(attr(srcRect, '', 'l')),
            top: percentage(attr(srcRect, '', 't')),
            right: percentage(attr(srcRect, '', 'r')),
            bottom: percentage(attr(srcRect, '', 'b')),
          }
        : NO_CROP,
    },
  ];
}

/**
 * The alt text and title Word stores beside a drawing.
 *
 * Worth surfacing on its own: Word populates `descr` from the original file
 * name for a pasted image often enough that it is a routine way a local path or
 * a client's name leaves a building.
 */
export function drawingLabels(root: XNode): { name?: string; description?: string }[] {
  const out: { name?: string; description?: string }[] = [];
  walkContent(root, (node) => {
    const isDocPr = node.ns === NS.wp && node.local === 'docPr';
    const isCNvPr = node.ns === NS.pic && node.local === 'cNvPr';
    if (!isDocPr && !isCNvPr) return undefined;
    const name = attr(node, '', 'name');
    const description = attr(node, '', 'descr') ?? attr(node, '', 'title');
    if (name || description) out.push({ name, description });
    return false;
  });
  return out;
}

/** Media whose bytes no browser will decode, so the reveal cannot be drawn. */
export function isUndecodable(part: string): boolean {
  return /\.(wmf|emf|tif|tiff|eps|pict)$/i.test(part);
}
